import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function ETFsListDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="List - ETFs API" description="Query list data via the ETFs API; request parameters, code examples and the response schema for the list endpoint."  canonicalUrl="https://axionquant.com/docs/01-market-data/ETFs/list" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">List - ETFs API</DocHeading>
      <div className="space-y-12" ref={sectionRef}>

        {/* List by Market */}
        <div id="market" className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /etfs/list/market
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all unique market values available in the etfs universe. Filter by market region (e.g., "america", "europe").
              </p>
            </div>
            <Links links={['/etfs/list/market']} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="List by Market" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">List by Market</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="etfs.list(market)"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/ETFs/list-market"></Code>
              </div>
            </div>
          </div>
        </div>
        {/* List by Country */}
        <div id="country" className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /etfs/list/country
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all unique country values available in the etfs universe. Filter by country name or code.
              </p>
            </div>
            <Links links={['/etfs/list/country']} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="List by Country" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">List by Country</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="etfs.list(country)"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/ETFs/list-country"></Code>
              </div>
            </div>
          </div>
        </div>
        {/* List by Currency */}
        <div id="currency" className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /etfs/list/currency
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all unique currency values available in the etfs universe. Filter by trading currency.
              </p>
            </div>
            <Links links={['/etfs/list/currency']} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="List by Currency" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">List by Currency</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="etfs.list(currency)"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/ETFs/list-currency"></Code>
              </div>
            </div>
          </div>
        </div>
        {/* List by Sector */}
        <div id="sector" className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /etfs/list/sector
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all unique sector values available in the etfs universe. Filter by sector classification.
              </p>
            </div>
            <Links links={['/etfs/list/sector']} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="List by Sector" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">List by Sector</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="etfs.list(sector)"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/ETFs/list-sector"></Code>
              </div>
            </div>
          </div>
        </div>
        {/* List by Industry */}
        <div id="industry" className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /etfs/list/industry
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all unique industry values available in the etfs universe. Filter by industry classification.
              </p>
            </div>
            <Links links={['/etfs/list/industry']} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="List by Industry" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">List by Industry</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="etfs.list(industry)"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/ETFs/list-industry"></Code>
              </div>
            </div>
          </div>
        </div>
        {/* List by Type */}
        <div id="type" className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /etfs/list/type
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all unique type values available in the etfs universe. Filter by security type.
              </p>
            </div>
            <Links links={['/etfs/list/type']} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="List by Type" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">List by Type</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="etfs.list(type)"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/ETFs/list-type"></Code>
              </div>
            </div>
          </div>
        </div>
        {/* List by Exchange */}
        <div id="exchange" className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /etfs/list/exchange
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all unique exchange values available in the etfs universe. Filter by exchange code.
              </p>
            </div>
            <Links links={['/etfs/list/exchange']} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="List by Exchange" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">List by Exchange</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="etfs.list(exchange)"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/ETFs/list-exchange"></Code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
